const isProbablyHtml = (value) => /<[^>]+>/.test(String(value || ""));

const sanitizeHtml = (value, options = {}) => {
  const source = String(value || "").trim();
  if (!source) return "";

  const normalizeUrlValue = (raw) => {
    const text = String(raw || "")
      .replace(/`/g, "")
      .trim();
    return text.replace(/^[\s"'“”‘’]+|[\s"'“”‘’]+$/g, "").trim();
  };

  const allowedTags = options.allowedTags || [
    "P",
    "BR",
    "B",
    "STRONG",
    "I",
    "EM",
    "U",
    "UL",
    "OL",
    "LI",
    "A",
    "H1",
    "H2",
    "H3",
    "H4",
    "BLOCKQUOTE",
    "CODE",
    "PRE",
    "HR",
    "IMG",
    "SPAN",
    "TABLE",
    "THEAD",
    "TBODY",
    "TFOOT",
    "TR",
    "TD",
    "TH",
    "COLGROUP",
    "COL",
  ];

  try {
    if (typeof DOMParser === "undefined") return source.replace(/<\/?[^>]+>/g, "");

    const allowed = new Set(allowedTags);
    const doc = new DOMParser().parseFromString(source, "text/html");

    const clean = (root) => {
      const children = Array.from(root.children || []);
      for (const el of children) {
        const tag = el.tagName;

        if (!allowed.has(tag)) {
          const parent = el.parentNode;
          while (el.firstChild) parent.insertBefore(el.firstChild, el);
          parent.removeChild(el);
          continue;
        }

        const rawImageSrc = tag === "IMG"
          ? [
              normalizeUrlValue(el.getAttribute("src")),
              normalizeUrlValue(el.getAttribute("data-url")),
              normalizeUrlValue(el.getAttribute("data-src")),
            ].find(Boolean) || ""
          : "";

        const attrs = Array.from(el.attributes || []);
        for (const attr of attrs) {
          const name = attr.name.toLowerCase();
          const isAnchor = tag === "A";
          const isImg = tag === "IMG";
          const isCell = tag === "TD" || tag === "TH";
          const isCol = tag === "COL";

          const allowedAttr =
            (isAnchor && (name === "href" || name === "target" || name === "rel")) ||
            (isImg && (name === "src" || name === "alt" || name === "title")) ||
            (isCell && (name === "colspan" || name === "rowspan")) ||
            (isCol && (name === "span" || name === "width")) ||
            false;

          if (!allowedAttr) el.removeAttribute(attr.name);
        }

        if (tag === "A") {
          const href = normalizeUrlValue(el.getAttribute("href"));
          if (href) {
            try {
              const resolved = new URL(href, window.location.origin);
              const protocol = resolved.protocol.toLowerCase();
              if (protocol !== "http:" && protocol !== "https:" && protocol !== "mailto:") {
                el.removeAttribute("href");
              } else {
                el.setAttribute("href", resolved.toString());
                el.setAttribute("target", "_blank");
                el.setAttribute("rel", "noreferrer noopener");
              }
            } catch {
              el.removeAttribute("href");
            }
          }
        }

        if (tag === "IMG") {
          const src = rawImageSrc;
          if (!src) {
            el.removeAttribute("src");
          } else {
            try {
              const resolved = new URL(src, window.location.origin);
              const protocol = resolved.protocol.toLowerCase();
              if (protocol !== "http:" && protocol !== "https:") {
                el.removeAttribute("src");
              } else {
                el.setAttribute("src", resolved.toString());
              }
            } catch {
              el.removeAttribute("src");
            }
          }

          el.style.maxWidth = "100%";
          el.style.height = "auto";
          el.style.display = "block";
          el.style.borderRadius = "10px";
        }

        if (tag === "P") el.style.margin = "0 0 12px 0";
        if (tag === "H1") el.style.margin = "0 0 12px 0";
        if (tag === "H2") el.style.margin = "0 0 10px 0";
        if (tag === "H3" || tag === "H4") el.style.margin = "0 0 8px 0";
        if (tag === "UL" || tag === "OL") {
          el.style.margin = "0 0 12px 18px";
          el.style.padding = "0";
        }
        if (tag === "PRE") {
          el.style.whiteSpace = "pre-wrap";
          el.style.margin = "0 0 12px 0";
        }
        if (tag === "TABLE") {
          el.style.width = "100%";
          el.style.borderCollapse = "collapse";
          el.style.margin = "0 0 12px 0";
        }
        if (tag === "TD" || tag === "TH") {
          el.style.border = "1px solid rgba(0,0,0,0.08)";
          el.style.padding = "8px 10px";
          el.style.verticalAlign = "top";
        }
        if (tag === "TH") {
          el.style.fontWeight = "700";
          el.style.background = "rgba(0,0,0,0.03)";
        }

        clean(el);
      }
    };

    clean(doc.body);

    if (doc.body.lastElementChild?.tagName === "P") {
      doc.body.lastElementChild.style.margin = "0";
    }

    return doc.body.innerHTML;
  } catch {
    return source.replace(/<\/?[^>]+>/g, "");
  }
};

export { sanitizeHtml, isProbablyHtml };
